import React, { useContext, useEffect } from 'react';
import { css } from '@compiled/react';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import { useLoaderData } from 'react-router-dom';

import { sendScreenEvent } from 'src/utils/analytics';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { HelmetContext } from 'src/components/Helmet';
import { ContentBlock } from 'src/pages/hubs/ResourcesAndSupportPage/children/ContentBlock';
import { getImageAttrs } from 'src/utils/contentful';
import { useReferrer } from 'src/hooks/useReferrer';
import { ContactUsSection } from 'src/components/ContactUsSection';
import { AssociatedContent } from 'src/types/contentful/base-content';
import { getContentLink, getContentLinkLabel, getContentLinkTarget } from 'src/pages/utils/utils';
import { Hero } from 'src/components/Hero';
import { isResourcesAndSupportEnabled } from 'src/utils/featureGate';

import { ResourcesAndSupportHubLoaderProps } from './loader';

export const ResourcesAndSupportPage: React.FC = () => {
  const { user, isLoading, hasError } = useAtlassianUser();
  const helmet = useContext(HelmetContext);
  const { referrerPath, isInitialized } = useReferrer();

  const { resourcesAndSupportHubData } = useLoaderData() as ResourcesAndSupportHubLoaderProps;

  helmet.setHelmet({
    title: `${resourcesAndSupportHubData.titleExternal} | Learning - Atlassian Community`,
    seoTags: resourcesAndSupportHubData.seo,
  });

  useEffect(() => {
    if (!isLoading && !hasError && isInitialized) {
      sendScreenEvent('resourcesAndSupportHubScreen', { learningReferrerPath: referrerPath }, user?.account_id);
    }
  }, [user, isLoading, hasError, referrerPath, isInitialized]);

  // TODO: remove this once ALM integration is enabled
  if (!isResourcesAndSupportEnabled()) {
    window.location.href = '/learning';

    return null;
  }

  const contactUsLink = resourcesAndSupportHubData.contactUs?.contentLinksCollection?.items[0] as AssociatedContent;

  return (
    <main data-testid="resources-and-support-hub" css={pageContainerStyles}>
      <Hero
        title={resourcesAndSupportHubData.hero.titleExternal}
        subtitle={resourcesAndSupportHubData.hero.subtitle}
        eyebrowText={resourcesAndSupportHubData.hero.eyebrowText}
        bottomImageSrc={resourcesAndSupportHubData.hero.asset?.image?.url}
      />
      <div data-testid="resources-and-support-content-section" css={outerContentContainerStyles}>
        <div css={innerContentContainerStyles}>
          {!!resourcesAndSupportHubData.linkedContentBlockCollection.items.length &&
            resourcesAndSupportHubData.linkedContentBlockCollection.items.map((contentBlock) => (
              <ContentBlock
                key={contentBlock.sys.id}
                imageSrc={getImageAttrs(contentBlock.asset)?.src}
                title={contentBlock.titleExternal}
                subtitle={contentBlock.subtitle}
                associatedContent={contentBlock.associatedContentResourcesCollection.items}
                fullWidthFirstContentEntry={contentBlock.fullWidthFirstContentEntry}
              />
            ))}
        </div>
      </div>
      {contactUsLink && (
        <div css={contactUsSectionContainerStyles}>
          <ContactUsSection
            title={resourcesAndSupportHubData.contactUs.contentSectionTitle.contentSectionTitleExternal}
            subtitle={resourcesAndSupportHubData.contactUs.contentSectionTitle.contentSectionSubtitle}
            linkUrl={getContentLink(contactUsLink) ?? ''}
            linkLabel={getContentLinkLabel(contactUsLink) ?? ''}
            linkTarget={getContentLinkTarget(contactUsLink)}
          />
        </div>
      )}
    </main>
  );
};

const pageContainerStyles = css({
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  alignItems: 'center',
  marginTop: '136px',

  '@media (max-width: 1279px)': {
    marginTop: '0',
  },
});

const outerContentContainerStyles = css({
  width: '100%',
  display: 'flex',
  padding: `${token('space.800')} 0`,
  flexDirection: 'column',
  alignItems: 'flex-start',
  background: token('elevation.surface.sunken'),

  [media.below.md]: {
    padding: `${token('space.400')} ${token('space.300')}`,
  },
  [media.below.xs]: {
    padding: `${token('space.400')} ${token('space.200')}`,
  },
});

const innerContentContainerStyles = css({
  background: token('elevation.surface.sunken'),
  maxWidth: '1024px',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  rowGap: token('space.400'),
  alignSelf: 'center',

  [media.below.md]: {
    rowGap: token('space.300'),
  },

  [media.below.xs]: {
    rowGap: token('space.200'),
  },
});

const contactUsSectionContainerStyles = css({
  marginTop: token('space.1000'),
});
